// Manager-mode vendor portal pages that mirror the mobile Manager app:
// Dispatch Queue, Analytics, Technician Locations. Reuse the web DataTable /
// KpiStat / primitives + the react-query polling + inline-invalidate pattern.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Inbox, Building2, MapPin, Navigation, AlertTriangle, Bell, TrendingUp, Clock } from 'lucide-react';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { KpiStat, StatusPill, PriorityPill, EmptyState } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/Toast';
import {
  vendorApi, type DispatchQueueItem, type VendorAnalytics, type TechnicianLocation, type ManagerNotification,
} from '@/lib/vendor-api';

// Local "seen" state for the manager notification feed (derived + ID-less feed;
// unread = items newer than last open). Persisted in localStorage.
const SEEN_KEY = 'iosentra_vendor_mgr_notif_seen';
export function markManagerNotificationsSeen(newestIso: string) {
  const prev = localStorage.getItem(SEEN_KEY);
  if (!prev || new Date(newestIso).getTime() > new Date(prev).getTime()) localStorage.setItem(SEEN_KEY, newestIso);
}
export function useManagerUnread(): number {
  const { data } = useQuery({ queryKey: ['v-mgr-notifications'], queryFn: () => vendorApi.managerNotifications(), refetchInterval: 30_000, staleTime: 15_000 });
  if (!data?.length) return 0;
  const seen = localStorage.getItem(SEEN_KEY);
  if (!seen) return data.length;
  const mark = new Date(seen).getTime();
  return data.filter((n) => new Date(n.at).getTime() > mark).length;
}

// Compact ms → "12m"/"3h"/"2d".
function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

type QStatus = 'awaiting' | 'rejected' | 'expired' | 'cancelled';

// ── Dispatch Queue ──────────────────────────────────────────────────────────
export function VendorDispatchPage() {
  const { t } = useTranslation(['vendor', 'common']);
  const toast = useToast();
  const qclient = useQueryClient();
  const [status, setStatus] = useState<QStatus>('awaiting');
  const { data, isLoading } = useQuery({ queryKey: ['v-dispatch', status], queryFn: () => vendorApi.dispatchQueue(status), refetchInterval: 15_000 });

  const cancel = async (item: DispatchQueueItem) => {
    try {
      await vendorApi.cancelOffer(item.tenantId, item.ticketId);
      qclient.invalidateQueries({ queryKey: ['v-dispatch'] });
      qclient.invalidateQueries({ queryKey: ['v-dashboard'] });
      toast.success(t('vendor:dispatch.cancelled', { defaultValue: 'Offer cancelled' }));
    } catch { toast.error(t('vendor:common.failed', { defaultValue: 'Could not complete' })); }
  };

  const cols: Column<DispatchQueueItem & { id: string }>[] = [
    { key: 'ticketNumber', header: t('vendor:dispatch.col.ticket', { defaultValue: 'Ticket' }), render: (r) => <span className="tk-num font-medium">{r.ticketNumber}</span> },
    { key: 'title', header: t('vendor:dispatch.col.title', { defaultValue: 'Title' }), grow: true, render: (r) => <span className="truncate">{r.title}</span> },
    { key: 'tenantName', header: t('vendor:dispatch.col.customer', { defaultValue: 'Customer' }) },
    { key: 'priority', header: t('vendor:dispatch.col.priority', { defaultValue: 'Priority' }), render: (r) => <PriorityPill priority={r.priority} /> },
    { key: 'technicianName', header: t('vendor:dispatch.col.tech', { defaultValue: 'Technician' }), render: (r) => r.technicianName ?? <span className="text-muted">—</span> },
    {
      key: 'age', header: t('vendor:dispatch.col.age', { defaultValue: 'Age' }),
      render: (r) => status === 'awaiting' && r.expiresInMs != null
        ? <span className={r.expiresInMs < 5 * 60_000 ? 'text-critical' : ''}>{t('vendor:dispatch.expires', { defaultValue: 'exp {{t}}', t: fmtMs(r.expiresInMs) })}</span>
        : <span className="text-muted">{fmtMs(r.ageMs)}</span>,
    },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          <a href={`/vendor/tickets/${r.tenantId}/${r.ticketId}`} className="tk-btn-ghost px-2 py-1 text-xs">{t('vendor:dispatch.reassign', { defaultValue: 'Reassign' })}</a>
          {status === 'awaiting' && <button onClick={() => cancel(r)} className="tk-btn-ghost px-2 py-1 text-xs text-critical">{t('vendor:dispatch.cancel', { defaultValue: 'Cancel' })}</button>}
        </div>
      ),
    },
  ];

  const rows = (data ?? []).map((o) => ({ ...o, id: o.offerId }));
  return (
    <div className="space-y-4">
      <PageHeader title={t('vendor:dispatch.title', { defaultValue: 'Dispatch queue' })} />
      <div className="flex gap-1.5">
        {(['awaiting', 'rejected', 'expired', 'cancelled'] as QStatus[]).map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`rounded-md px-3 py-1.5 text-sm ${status === s ? 'bg-primary/15 text-primary ring-1 ring-primary/40' : 'text-muted hover:bg-white/5'}`}>
            {t(`vendor:dispatch.${s}`, { defaultValue: s })}
          </button>
        ))}
      </div>
      <DataTable columns={cols} rows={rows} loading={isLoading} emptyIcon={Inbox}
        emptyTitle={t('vendor:dispatch.empty', { defaultValue: 'Nothing here' })} />
    </div>
  );
}

// ── Analytics ───────────────────────────────────────────────────────────────
const TIP_STYLE = { background: '#0b1220', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, fontSize: 12 };
export function VendorAnalyticsPage() {
  const { t } = useTranslation(['vendor', 'common']);
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({ queryKey: ['v-analytics', days], queryFn: () => vendorApi.analytics(days) });
  const slaColor = (v: number | null | undefined) => (v == null ? '#64748b' : v >= 90 ? '#22c55e' : v >= 75 ? '#f59e0b' : '#ef4444');
  const pct = (v: number | null | undefined) => (v == null ? '—' : `${v}%`);

  const chartData = (data?.byCustomer ?? []).map((c) => ({ name: c.tenantName || c.tenantId.slice(0, 8), sla: c.slaCompliance ?? 0, resolved: c.resolved }));

  return (
    <div className="space-y-4">
      <PageHeader title={t('vendor:analytics.title', { defaultValue: 'Analytics' })} />
      <div className="flex gap-1.5">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`rounded-md px-3 py-1.5 text-sm ${days === d ? 'bg-primary/15 text-primary ring-1 ring-primary/40' : 'text-muted hover:bg-white/5'}`}>
            {t('vendor:analytics.days', { defaultValue: '{{n}} days', n: d })}
          </button>
        ))}
      </div>
      {isLoading || !data ? <div className="tk-card h-40 animate-pulse" /> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiStat label={t('vendor:analytics.compliance', { defaultValue: 'SLA compliance' })} value={pct(data.sla.compliancePct)} tone={data.sla.compliancePct != null && data.sla.compliancePct >= 90 ? 'ok' : 'warning'} />
            <KpiStat label={t('vendor:analytics.resolved', { defaultValue: 'Resolved' })} value={data.sla.resolved} tone="info" />
            <KpiStat label={t('vendor:analytics.acceptRate', { defaultValue: 'Acceptance' })} value={pct(data.offers.acceptanceRatePct)} tone="info" />
            <KpiStat label={t('vendor:analytics.avgAccept', { defaultValue: 'Avg to accept' })} value={data.offers.avgTimeToAcceptMs != null ? fmtMs(data.offers.avgTimeToAcceptMs) : '—'} tone="info" />
          </div>
          <div className="tk-card">
            <div className="mb-3 text-sm font-medium">{t('vendor:analytics.byCustomer', { defaultValue: 'SLA compliance by customer' })}</div>
            {chartData.length === 0 ? <EmptyState icon={Building2} title={t('vendor:analytics.noData', { defaultValue: 'No data in this window' })} /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} domain={[0, 100]} unit="%" />
                  <RTooltip contentStyle={TIP_STYLE} />
                  <Bar dataKey="sla" radius={[4, 4, 0, 0]}>
                    {chartData.map((c, i) => <Cell key={i} fill={slaColor(c.sla)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Technician locations ─────────────────────────────────────────────────────
export function VendorLocationsPage() {
  const { t } = useTranslation(['vendor', 'common']);
  const { data, isLoading } = useQuery({ queryKey: ['v-tech-locations'], queryFn: () => vendorApi.technicianLocations(), refetchInterval: 30_000 });

  const openMap = (l: TechnicianLocation) => window.open(`https://maps.google.com/?q=${l.lat},${l.lng}`, '_blank');
  const cols: Column<TechnicianLocation & { id: string }>[] = [
    { key: 'name', header: t('vendor:locations.col.tech', { defaultValue: 'Technician' }), render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'operationalStatus', header: t('vendor:locations.col.status', { defaultValue: 'Status' }), render: (r) => <StatusPill status={String(r.operationalStatus).toLowerCase()} /> },
    { key: 'coords', header: t('vendor:locations.col.coords', { defaultValue: 'Location' }), render: (r) => <span className="tk-num text-xs text-muted">{r.lat.toFixed(4)}, {r.lng.toFixed(4)}</span> },
    { key: 'at', header: t('vendor:locations.col.seen', { defaultValue: 'Seen' }), render: (r) => <span className="text-muted">{fmtMs(Date.now() - new Date(r.at).getTime())}</span> },
    { key: 'actions', header: '', align: 'right', render: (r) => <button onClick={() => openMap(r)} className="tk-btn-ghost px-2 py-1 text-xs"><Navigation size={13} className="me-1 inline" />{t('vendor:locations.open', { defaultValue: 'Open' })}</button> },
  ];
  const rows = (data ?? []).map((l) => ({ ...l, id: l.technicianId }));
  return (
    <div className="space-y-4">
      <PageHeader title={t('vendor:locations.title', { defaultValue: 'Technician locations' })} />
      <DataTable columns={cols} rows={rows} loading={isLoading} emptyIcon={MapPin}
        emptyTitle={t('vendor:locations.empty', { defaultValue: 'No technician locations yet' })}
        emptyHint={t('vendor:locations.emptyHint', { defaultValue: 'Locations appear when technicians share them.' })} />
    </div>
  );
}

// ── Manager notifications ─────────────────────────────────────────────────────
const NOTIF_ICON = { offer: Clock, escalation: TrendingUp, sla_breached: AlertTriangle } as const;
const SEV_COLOR = { info: 'text-info', warning: 'text-warning', critical: 'text-critical' } as const;
export function VendorNotificationsPage() {
  const { t } = useTranslation(['vendor', 'common']);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['v-mgr-notifications'], queryFn: () => vendorApi.managerNotifications(), refetchInterval: 20_000 });

  // Mark all seen once the freshest item is known.
  const newest = data?.[0]?.at;
  useEffect(() => { if (newest) markManagerNotificationsSeen(newest); }, [newest]);

  return (
    <div className="space-y-4">
      <PageHeader title={t('vendor:notif.title', { defaultValue: 'Notifications' })} />
      {isLoading ? <div className="tk-card h-40 animate-pulse" /> :
        (data ?? []).length === 0 ? <EmptyState icon={Bell} title={t('vendor:notif.empty', { defaultValue: 'Nothing needs your attention' })} /> : (
          <div className="tk-card divide-y divide-white/5">
            {(data ?? []).map((n: ManagerNotification) => {
              const Icon = NOTIF_ICON[n.kind as keyof typeof NOTIF_ICON] ?? Bell;
              const color = SEV_COLOR[n.severity] ?? 'text-info';
              return (
                <button key={n.id} onClick={() => n.ticketId && navigate(`/vendor/tickets/${n.tenantId}/${n.ticketId}`)}
                  className="flex w-full items-center gap-3 py-3 text-start hover:bg-white/5">
                  <Icon size={16} className={color} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{n.title}</span>
                    <span className="block text-xs text-muted">{n.tenantName}</span>
                  </span>
                  <span className="text-xs text-muted">{new Date(n.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </button>
              );
            })}
          </div>
        )}
    </div>
  );
}

// Kept for potential reuse (unused import guard).
void AlertTriangle;
